import classNames from 'classnames';

import {Renderer as BaseRenderer, escapeHtml, type Tokens, type MarkedOptions} from '@time-webkit/marked';

import {TypographySize, typographyStyles} from '@time-webkit/all/hooks/typography';

import * as PostUtils from 'utils/post_utils';
import * as TextFormatting from 'utils/text_formatting';
import * as Emoticons from 'utils/emoticons';

import {getScheme, isUrlSafe, shouldOpenInNewTab} from 'utils/url';

import appPostStyles from 'packages/editor/src/extensions/app-post-style.module.css';

import styles from './styles.module.css';
import {parseImageDimensions} from './helpers';

export default class Renderer extends BaseRenderer {
    private formattingOptions: TextFormatting.TextFormattingOptions;
    public constructor(
        options: MarkedOptions,
        formattingOptions: TextFormatting.TextFormattingOptions,
    ) {
        super(options);

        this.heading = this.heading.bind(this);
        this.paragraph = this.paragraph.bind(this);
        this.text = this.text.bind(this);

        this.formattingOptions = formattingOptions;
    }

    public code({text: code, lang: language}: Tokens.Code): string {
        let usedLanguage = language || '';
        usedLanguage = usedLanguage.toLowerCase();

        if (usedLanguage === 'tex' || usedLanguage === 'latex') {
            return `<div data-latex="${escapeHtml(code, true)}"></div>`;
        }

        let searchedContent = '';

        if (this.formattingOptions.searchPatterns) {
            const tokens = new Map();

            let searched = escapeHtml(code, true);
            searched = TextFormatting.highlightSearchTerms(searched, tokens, this.formattingOptions.searchPatterns);

            if (tokens.size > 0) {
                searched = TextFormatting.replaceTokens(searched, tokens);

                searchedContent = '<div class="post-code__search-highlighting">' + searched + '</div>';
            }
        }

        return (
            '<div data-codeblock-code="' +
            escapeHtml(code, true) +
            '" ' +
            'data-codeblock-language="' +
            escapeHtml(usedLanguage, true) +
            '" ' +
            'data-codeblock-searchedcontent="' +
            escapeHtml(searchedContent, true) +
            '"></div>'
        );
    }

    public codespan({text}: Tokens.Codespan) {
        let output = escapeHtml(text, true);

        if (this.formattingOptions.searchPatterns) {
            const tokens = new Map();
            output = TextFormatting.highlightSearchTerms(output, tokens, this.formattingOptions.searchPatterns);
            output = TextFormatting.replaceTokens(output, tokens);
        }

        return `<span class="codespan__pre-wrap"><code>${output}</code></span>`;
    }

    public br() {
        if (this.formattingOptions.singleline) {
            return ' ';
        }

        return super.br({type: 'br', raw: '<br />'});
    }

    public image({title, text, href}: Tokens.Image) {
        const dimensions = parseImageDimensions(href);

        let src = dimensions.href;
        src = PostUtils.getImageSrc(src, this.formattingOptions.proxyImages, this.formattingOptions.additionalSiteURLs);

        let out = `<img src="${src}" alt="${text}"`;
        if (title) {
            out += ` title="${title}"`;
        }
        if (dimensions.width) {
            out += ` width="${dimensions.width}"`;
        }
        if (dimensions.height) {
            out += ` height="${dimensions.height}"`;
        }
        out += ' class="markdown-inline-img">';
        return out;
    }

    public heading({tokens, depth: level}: Tokens.Heading) {
        const text = this.parser.parseInline(tokens);
        const className = classNames(
            styles.markdown__heading,
            'app_post_markdown__heading',
        );
        return `<h${level} class="${className}">${text}</h${level}>`;
    }

    public blockquote({tokens}: Tokens.Blockquote) {
        const body = this.parser.parse(tokens);
        return `<blockquote class="${appPostStyles.blockquote}">${body}</blockquote>`;
    }

    public link({href, tokens, title, text: tokenText, isUrl = false}: Tokens.Link & {isUrl?: boolean}) {
        const text = isUrl ? escapeHtml(tokenText) : this.parser.parseInline(tokens);
        let outHref = escapeHtml(href);

        if (!href.startsWith('/')) {
            const scheme = getScheme(href);
            if (!scheme) {
                outHref = `http://${outHref}`;
            } else if (isUrl && this.formattingOptions.autolinkedUrlSchemes) {
                const isValidUrl = this.formattingOptions.autolinkedUrlSchemes.indexOf(scheme.toLowerCase()) !== -1;

                if (!isValidUrl) {
                    return text;
                }
            }
        }

        if (!isUrlSafe(unescapeHtmlEntities(href))) {
            return text;
        }

        if (this.formattingOptions.siteURL && this.formattingOptions.additionalSiteURLs) {
            const urlToReplace = this.formattingOptions.additionalSiteURLs
                .split(' ')
                .find((u) => outHref.startsWith(u));

            if (urlToReplace) {
                outHref = outHref.replace(urlToReplace, this.formattingOptions.siteURL);
            }
        }

        let output = '<a class="theme markdown__link';

        if (this.formattingOptions.searchPatterns) {
            for (const pattern of this.formattingOptions.searchPatterns) {
                if (pattern.pattern.test(href)) {
                    output += ' search-highlight';
                    break;
                }
            }
        }

        output += `" href="${outHref}" rel="noreferrer"`;

        const openInNewTab = shouldOpenInNewTab(
            outHref,
            this.formattingOptions.siteURL,
            this.formattingOptions.managedResourcePaths,
        );

        if (openInNewTab || !this.formattingOptions.siteURL) {
            output += ' target="_blank"';
        } else {
            output += ` data-link="${outHref.replace(this.formattingOptions.siteURL, '')}"`;
        }

        if (title) {
            output += ` title="${escapeHtml(title)}"`;
        }

        // remove any links added to the text by hashtag or mention parsing since they'll break this link
        output += '>' + text.replace(/<\/?a[^>]*>/g, '') + '</a>';

        return output;
    }

    public paragraph({tokens}: Tokens.Paragraph) {
        const text = this.parser.parseInline(tokens);
        if (this.formattingOptions.singleline) {
            let result;
            if (text.includes('class="markdown-inline-img"')) {
                /*
                 ** use a div tag instead of a p tag to allow other divs to be nested,
                 ** which avoids errors of incorrect DOM nesting (<div> inside <p>)
                 */
                result = `<div class="${classNames(
                    typographyStyles[TypographySize.BodyL],
                    typographyStyles.base,
                    typographyStyles['base-color'],
                    'markdown__paragraph-inline',
                )}">${text}</div>`;
            } else {
                result = `<p class="${classNames(
                    typographyStyles[TypographySize.BodyL],
                    typographyStyles.base,
                    typographyStyles['base-color'],
                    styles.text,
                    'markdown__paragraph-inline',
                )}">${text}</p>`;
            }
            return result;
        }

        return `<p class="${classNames(
            typographyStyles[TypographySize.BodyL],
            typographyStyles.base,
            typographyStyles['base-color'],
            styles.text,
        )}">${text}</p>`;
    }

    public table({header, rows}: Tokens.Table) {
        let head = '';
        let body = '';

        let cell = '';
        for (const headCell of header) {
            cell += this.tablecell(headCell);
        }
        head += this.tablerow({text: cell});

        for (const row of rows) {
            cell = '';
            for (const rowCell of row) {
                cell += this.tablecell(rowCell);
            }
            body += this.tablerow({text: cell});
        }
        if (body) {
            body = `<tbody>${body}</tbody>`;
        }

        return `<div class="table-responsive"><table class="markdown__table"><thead>${head}</thead>${body}</table></div>`;
    }

    public tablerow({text}: Tokens.TableRow) {
        return `<tr>${text}</tr>`;
    }

    public tablecell(
        tableCell: Tokens.TableCell,
    ) {
        return super.tablecell(tableCell).trim();
    }

    public list({start, ordered, items}: Tokens.List) {
        const type = ordered ? 'ol' : 'ul';
        const startNum = typeof start === 'string' ? 1 : start;

        let content = '';

        for (const item of items) {
            content += this.listitem(item);
        }

        let output = `<${type} className="markdown__list"`;

        if (ordered && start !== undefined) {
            // The CSS that we use for lists hides the actual counter and uses ::before to simulate one so that we can
            // style it properly. We need to use a CSS counter to tell the ::before elements which numbers to show.
            output += ` start="${startNum}"`;
        }

        output += `>\n${content}</${type}>`;

        return output;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public listitem(item: Tokens.ListItem) {
        let itemBody = '';
        let itemAttributes = '';

        if (item.task) {
            itemAttributes = ' class="list-item--task-list"';
            const checkbox = this.checkbox({checked: Boolean(item.checked)});
            if (item.loose) {
                const itemFirstChild = item.tokens[0];
                if (itemFirstChild && itemFirstChild.type === 'paragraph') {
                    itemFirstChild.text = checkbox + ' ' + itemFirstChild.text;
                    const itemFirstChildFirstChild = itemFirstChild.tokens?.[0];
                    if (itemFirstChildFirstChild && itemFirstChildFirstChild.type === 'text') {
                        itemFirstChildFirstChild.text = checkbox + ' ' + escapeHtml(itemFirstChildFirstChild.text);
                        itemFirstChildFirstChild.escaped = true;
                    }
                } else {
                    item.tokens.unshift({
                        type: 'text',
                        raw: checkbox + ' ',
                        text: checkbox + ' ',
                        escaped: true,
                    });
                }
            } else {
                itemBody += checkbox + ' ';
            }
        }

        itemBody += this.parser.parse(item.tokens, item.loose);

        // Added a span because if not whitespace nodes only
        // works in Firefox but not in Webkit
        return `<li${itemAttributes}><span>${itemBody}</span></li>`;
    }

    public checkbox({checked}: Tokens.Checkbox) {
        const checkedAttr = checked ? 'checked="true" ' : '';
        return `<input ${checkedAttr}disabled="true" type="checkbox">`;
    }

    public text(token: Tokens.Text | Tokens.Escape) {
        // При копировании пути файла на Windows теряется знак \, если после него идет !
        // https://jira.tcsbank.ru/browse/TIME-11743
        if (token.type === 'escape' && token.text === '!') {
            return '\\!';
        }

        let text = super.text(token);
        text = text.replace(/&#39;/g, '&apos;');

        // из-за вложенной структуры токенов неправильно отображались почта и ссылки в списках
        // в doFormatText попадёт только последний текстовый токен
        if (token.type === 'text' && (token.escaped || token.tokens)) {
            return text;
        }
        return TextFormatting.doFormatText(text, this.formattingOptions);
    }

    public strong({tokens}: Tokens.Strong): string {
        return `<strong class='Body-L-Bold'>${this.parser.parseInline(tokens)}</strong>`;
    }

    public emoji(token: Tokens.Generic) {
        const emoji = token.isUnicode ? (
            `<span class="emoticon emoticon--unicode">${token.raw}</span>`
        ) : Emoticons.renderEmoji(token.text.replaceAll(':', ''), token.raw);

        if (!this.formattingOptions.singleline && token?.isSingle) {
            return `<span class='all-emoji'>${emoji}</span>`;
        }

        return emoji;
    }
}

// Marked helper functions that should probably just be exported

function unescapeHtmlEntities(html: string) {
    return html.replace(/&([#\w]+);/g, (_, m) => {
        const n = m.toLowerCase();
        if (n === 'colon') {
            return ':';
        } else if (n.charAt(0) === '#') {
            return n.charAt(1) === 'x' ? String.fromCharCode(parseInt(n.substring(2), 16)) : String.fromCharCode(Number(n.substring(1)));
        }
        return '';
    });
}
